/** Pure helpers for the versions UI (CONCEPT.md §15). */
import type { PlaySessionSummary, PublicVersion, StoryVersion } from "@storyai/shared";

/** A choice in the landing page's version chooser. "Original" is the base story. */
export interface VersionChoice {
  /** null = Original */
  versionId: string | null;
  storyId: string;
  title: string;
  description: string;
  language: string | null;
  /** True when the version has learning mode on (authors only; players never see this). */
  learning: boolean;
}

export function versionChoices(baseStoryId: string, versions: PublicVersion[]): VersionChoice[] {
  return [
    { versionId: null, storyId: baseStoryId, title: "Original", description: "", language: null, learning: false },
    ...versions.map((v) => ({
      versionId: v.id,
      storyId: v.storyId,
      title: v.title,
      description: v.description,
      language: v.language,
      learning: "learningSetup" in v ? Boolean((v as { learningSetup?: { enabled?: boolean } | null }).learningSetup?.enabled) : false,
    })),
  ];
}

/**
 * Which choice to preselect: the `?version=` id when it matches an available
 * version, else Original.
 */
export function pickInitialChoice(choices: VersionChoice[], requestedVersionId: string | null): VersionChoice {
  if (requestedVersionId) {
    const hit = choices.find((c) => c.versionId === requestedVersionId);
    if (hit) return hit;
  }
  return choices[0]!;
}

/**
 * Merges session lists from the base and its versions into one list, most
 * recently played first, labelling each session with its version title.
 */
export function mergeSessionLists(
  lists: { versionTitle: string | null; sessions: PlaySessionSummary[] }[],
): PlaySessionSummary[] {
  const out: PlaySessionSummary[] = [];
  for (const l of lists) {
    for (const s of l.sessions) out.push({ ...s, versionTitle: s.versionTitle ?? l.versionTitle });
  }
  return out.sort((a, b) => b.lastPlayedAt.localeCompare(a.lastPlayedAt));
}

export function versionStatusLabel(v: StoryVersion): string {
  if (v.status === "generating") {
    const p = v.progress;
    return p && p.total > 0 ? `Generating ${p.done}/${p.total}` : "Generating";
  }
  if (v.status === "failed") return "Failed";
  return v.enabled ? "Ready" : "Hidden";
}
